import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface Category {
  id: string;
  name: string;
  color: string;
  text_color: string;
  sort_order: number;
}

const PRESET_COLORS = [
  { color: '#3b82f6', name: 'Blue' },
  { color: '#ef4444', name: 'Red' },
  { color: '#22c55e', name: 'Green' },
  { color: '#f59e0b', name: 'Orange' },
  { color: '#8b5cf6', name: 'Purple' },
  { color: '#ec4899', name: 'Pink' },
  { color: '#06b6d4', name: 'Cyan' },
  { color: '#84cc16', name: 'Lime' },
  { color: '#f97316', name: 'Deep Orange' },
  { color: '#6366f1', name: 'Indigo' },
];

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [maxCategories, setMaxCategories] = useState(5);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState('#3b82f6');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, limitRes] = await Promise.all([
        apiService.getCategories(),
        apiService.getCategoryLimit()
      ]);
      
      if (categoriesRes.success) {
        setCategories(categoriesRes.categories);
      }
      if (limitRes.success) {
        setMaxCategories(limitRes.maxCategories);
      }
    } catch (err: any) {
      setError('Failed to load categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormColor('#3b82f6');
    setShowAddForm(false);
    setEditingId(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      if (editingId) {
        // Update existing
        const res = await apiService.updateCategory(editingId, {
          name: formName.trim(),
          color: formColor
        });
        if (res.success) {
          setSuccess('Category updated successfully');
          fetchData();
          resetForm();
        } else {
          setError(res.error || 'Failed to update category');
        }
      } else {
        // Create new
        const res = await apiService.createCategory({
          name: formName.trim(),
          color: formColor
        });
        if (res.success) {
          setSuccess('Category created successfully');
          fetchData();
          resetForm();
        } else {
          setError(res.error || 'Failed to create category');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingId(category.id);
    setFormName(category.name);
    setFormColor(category.color);
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete category "${name}"? Tasks with this category will be updated.`)) {
      return;
    }

    try {
      const res = await apiService.deleteCategory(id);
      if (res.success) {
        setSuccess('Category deleted successfully');
        fetchData();
      } else {
        setError(res.error || 'Failed to delete category');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred');
    }
  };

  const getContrastColor = (hexColor: string) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ width: 30, height: 30, border: '3px solid #e5e7eb', borderTop: '3px solid var(--primary-color, #667eea)', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
          Task Categories
        </h3>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
          Create custom categories with colors to organize your tasks (max {maxCategories})
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#16a34a',
          marginBottom: '16px',
          fontSize: '0.875rem'
        }}>
          {success}
        </div>
      )}

      {/* Existing Categories */}
      <div style={{ marginBottom: '24px' }}>
        {categories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            color: '#6b7280'
          }}>
            <p style={{ margin: '0 0 16px 0' }}>No categories yet. Create your first one!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {categories.map(category => (
              <div
                key={category.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Color preview */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: category.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: category.text_color,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {category.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Category pill preview */}
                  <span style={{
                    padding: '6px 14px',
                    backgroundColor: category.color,
                    color: category.text_color,
                    borderRadius: '16px',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    {category.name}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEdit(category)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#374151',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(category.id, category.name)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fef2f2',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#dc2626',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm ? (
        <div style={{
          padding: '20px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600 }}>
            {editingId ? 'Edit Category' : 'Add New Category'}
          </h4>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                Category Name
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Work, Personal, Health"
                maxLength={30}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                Color
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {PRESET_COLORS.map(preset => (
                  <button
                    key={preset.color}
                    type="button"
                    onClick={() => setFormColor(preset.color)}
                    title={preset.name}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: preset.color,
                      border: formColor === preset.color ? '3px solid #1f2937' : '2px solid transparent',
                      cursor: 'pointer',
                      transition: 'transform 0.15s',
                      transform: formColor === preset.color ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="color"
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '36px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Or pick a custom color
                </span>
              </div>
            </div>

            {/* Preview */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                Preview
              </label>
              <span style={{
                display: 'inline-block',
                padding: '6px 14px',
                backgroundColor: formColor,
                color: getContrastColor(formColor),
                borderRadius: '16px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {formName || 'Category Name'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: 'var(--primary-color, #667eea)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {editingId ? 'Save Changes' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <button
          onClick={() => {
            if (categories.length >= maxCategories) {
              setError(`Maximum ${maxCategories} categories allowed. Delete one to add more.`);
              return;
            }
            setShowAddForm(true);
            setError('');
            setSuccess('');
          }}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: 'var(--primary-light, #f0f4ff)',
            border: '2px dashed var(--primary-color, #667eea)',
            borderRadius: '10px',
            color: 'var(--primary-color, #667eea)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: categories.length >= maxCategories ? 'not-allowed' : 'pointer',
            opacity: categories.length >= maxCategories ? 0.6 : 1
          }}
        >
          + Add Category ({categories.length}/{maxCategories})
        </button>
      )}
    </div>
  );
};

export default CategoryManagement;
